import React, { FC, useEffect, useState, useMemo } from 'react';
import { DateUtils } from '@jaam/commons';
import { ChevronDown20Filled, ChevronUp20Filled } from '@fluentui/react-icons';

type DateSelectorProps = {
  /**
   * Valeur contrôlée du composant. Si définie, la sélection du composant
   * sera toujours synchronisée avec `value`.
   */
  value?: Date | null;
  /**
   * Date par défaut (pour un usage non contrôlé). N’est prise en compte
   * que si `value` est `null` ou `undefined`.
   */
  defaultDate?: Date;
  /**
   * Callback déclenché à chaque fois que l’utilisateur sélectionne
   * un (year, month).
   */
  onChange: (date: Date) => void;
  /**
   * Classes CSS optionnelles.
   */
  className?: string;
};

const DateSelector: FC<DateSelectorProps> = ({
  value,
  defaultDate,
  onChange,
  className = '',
}) => {
  // S’assurer que Moment utilise la locale française

  /**
   * Déterminer la "date de référence" à utiliser :
   * 1) si `value` existe (et n’est pas null), on l’utilise (composant contrôlé)
   * 2) sinon on prend `defaultDate` si elle existe
   * 3) sinon on utilise la date du jour
   */
  const derivedDate = value ?? defaultDate ?? new Date();

  // On stocke l'année et le mois sélectionnés dans un state local
  const [selectedYear, setSelectedYear] = useState(derivedDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(derivedDate.getMonth());

  // Surveille si la prop `value` (contrôlée) change. Si oui, on synchronise.
  useEffect(() => {
    // Si `value` est défini (pas `null`), on synchronise
    // Sinon, on ne fait rien, car on est en mode "uncontrolled" (ou defaultDate).
    if (value) {
      setSelectedYear(value.getFullYear());
      setSelectedMonth(value.getMonth());
    }
  }, [value]);

  // Permet de basculer entre la vue "années" et "mois"
  const [view, setView] = useState<'years' | 'months'>('months');

  // Générer la liste de 31 années : 10 avant, l'année courante, +20 après
  const years = useMemo(() => {
    const startYear = selectedYear - 10;
    const totalYears = 10 + 1 + 20; // 31 total
    return Array.from({ length: totalYears }, (_, i) => startYear + i);
  }, [selectedYear]);

  // Mois en français (version courte/longue)

  const monthsShort = useMemo(() => DateUtils.localeData().monthsShort(), []);
  const monthsLong = useMemo(() => DateUtils.localeData().months(), []);

  const handleYearClick = (year: number) => {
    setSelectedYear(year);

    if (value) {
      const updatedDate = new Date(value);
      updatedDate.setFullYear(year);
      onChange(updatedDate);
    }

    setView('months');
  };

  const handleMonthClick = (monthIndex: number) => {
    setSelectedMonth(monthIndex);
    const newDate = new Date(selectedYear, monthIndex, 1);
    onChange(newDate);
  };

  return (
    <div
      className={`w-full max-w-[450px] p-3 rounded-xl bg-neutral-98 ${className}`}
    >
      {/* Affichage du mois et de l'année sélectionnés */}
      <div className="flex items-center justify-start gap-2 mb-3">
        <p className="font-medium">
          {monthsLong[selectedMonth]} {selectedYear}
        </p>
        <button
          type="button"
          onClick={() => setView(view === 'months' ? 'years' : 'months')}
        >
          {view === 'years' ? <ChevronUp20Filled /> : <ChevronDown20Filled />}
        </button>
      </div>

      {view === 'years' ? (
        // Liste des années (scrollable si on veut limiter la hauteur)
        <div className="grid grid-cols-4 gap-2 max-h-[160px] overflow-y-auto">
          {years.map((year) => (
            <button
              key={year}
              type="button"
              onClick={() => handleYearClick(year)}
              className={`p-2 text-sm rounded-full ${
                year === selectedYear
                  ? 'bg-primary text-white'
                  : 'hover:bg-blue-95'
              }`}
            >
              {year}
            </button>
          ))}
        </div>
      ) : (
        // Liste des mois
        <div className="grid grid-cols-3 gap-2">
          {monthsShort.map((monthLabel, index) => (
            <button
              key={monthLabel}
              type="button"
              onClick={() => handleMonthClick(index)}
              className={`p-2 text-sm rounded-full ${
                index === selectedMonth
                  ? 'bg-primary text-white'
                  : 'hover:bg-blue-95'
              }`}
            >
              {monthLabel}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

DateSelector.displayName = 'MonthYearSelector';

export { DateSelector };
